/**
 * <modoviz-sale-countdown>
 *
 * Counts down to one fixed, absolute end moment (data-end, Unix seconds) calculated server-side
 * from the section settings. No cookies, no localStorage, no per-visitor deadline.
 *
 * - On every update the remaining time is recalculated from the end moment (never "minus one").
 * - Returning to the tab (visibilitychange / pageshow / focus) triggers an immediate recalculation.
 * - The device clock drift is corrected once using the server's Date header, so a wrong device
 *   clock doesn't lead to a different deadline.
 * - Never negative values; after the end moment the section is hidden or the end message is shown.
 */
class ModovizSaleCountdown extends HTMLElement {
  /** @type {number} */
  #endMs = 0;
  /** @type {number} */
  #startMs = 0;
  /** @type {number} */
  #offsetSeconds = 0;
  /** Difference between server time and device time (ms). */
  #clockSkewMs = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {string} */
  #state = '';
  /** @type {Record<string, string>} */
  #lastText = {};

  connectedCallback() {
    // Prevent duplicate timers/listeners if the section gets reconnected.
    this.#teardown();

    this.#endMs = Number(this.dataset.end) * 1000;
    this.#startMs = Number(this.dataset.start) * 1000;
    this.#offsetSeconds = Number(this.dataset.offset) || 0;

    if (!Number.isFinite(this.#endMs) || this.#endMs <= 0) {
      this.hidden = true;
      return;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener('visibilitychange', this.#recheck, { signal });
    window.addEventListener('pageshow', this.#recheck, { signal });
    window.addEventListener('focus', this.#recheck, { signal });

    this.#localizeEndDate();
    this.#update();
    this.setAttribute('data-ready', '');
    this.#syncWithServerClock();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    clearTimeout(this.#timeout);
    this.#timeout = undefined;
    this.#abort?.abort();
    this.#abort = undefined;
  }

  #now() {
    return Date.now() + this.#clockSkewMs;
  }

  #recheck = () => {
    if (document.visibilityState === 'hidden') return;
    this.#update();
  };

  #update() {
    clearTimeout(this.#timeout);
    const now = this.#now();

    if (now >= this.#endMs) {
      this.#setState('ended');
      return; // done: no more timer, never restarts
    }

    if (this.#startMs > 0 && now < this.#startMs) {
      this.#setState('pending');
      // Don't wait for the start in one go (timers in background tabs don't run exactly); check regularly.
      this.#timeout = setTimeout(() => this.#update(), Math.min(this.#startMs - now, 15000));
      return;
    }

    this.#setState('active');
    const remainingMs = this.#endMs - now;
    this.#render(remainingMs);

    // Next update exactly at the next second transition of the remaining time.
    const delay = (remainingMs % 1000) + 20;
    this.#timeout = setTimeout(() => this.#update(), delay);
  }

  /** @param {string} state */
  #setState(state) {
    if (state === this.#state) return;
    const previous = this.#state;
    this.#state = state;
    // The first status matches the server render (no recalculation needed), except if JS corrects immediately.
    const needsHeightSync = previous !== '' || state !== 'active';

    const content = this.querySelector('[data-msc-content]');
    const message = this.querySelector('[data-msc-message]');
    const showMessage = this.dataset.afterEnd === 'message';

    if (state === 'pending') {
      this.hidden = true;
    } else if (state === 'active') {
      this.hidden = false;
      if (content) content.hidden = false;
      if (message) message.hidden = true;
    } else if (state === 'ended') {
      if (showMessage && message) {
        this.hidden = false;
        if (content) content.hidden = true;
        message.hidden = false;
      } else {
        // Hide completely, including the sale text and link.
        this.hidden = true;
        if (content) content.hidden = true;
      }
    }

    // offsetHeight itself forces a layout calculation, so calling it directly is enough.
    if (needsHeightSync) this.#syncHeaderGroupHeight();
  }

  /**
   * Keeps --header-group-height correct as the bar appears or disappears (same formula as the
   * inline script in layout/theme.liquid: sum of all children of #header-group).
   */
  #syncHeaderGroupHeight() {
    const group = document.querySelector('#header-group');
    if (!group) return;
    let total = 0;
    for (const child of group.children) {
      if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  }

  /** @param {number} remainingMs */
  #render(remainingMs) {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const showDays = this.dataset.showDays !== 'false';

    let days = Math.floor(totalSeconds / 86400);
    let hours = Math.floor((totalSeconds % 86400) / 3600);
    // If days are hidden, fold them into the hours so the total time still adds up.
    if (!showDays) {
      hours += days * 24;
      days = 0;
    }
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#write('days', days);
    this.#write('hours', hours);
    this.#write('minutes', minutes);
    this.#write('seconds', seconds);
  }

  /**
   * @param {string} unit
   * @param {number} value
   */
  #write(unit, value) {
    const el = this.querySelector(`[data-msc-${unit}]`);
    if (!el) return;
    const text = String(value).padStart(2, '0');
    if (this.#lastText[unit] === text) return;
    this.#lastText[unit] = text;
    el.textContent = text;
  }

  /**
   * Shows the end date in the storefront's language, but always in the store's timezone
   * (never the visitor's timezone). Falls back to the server-rendered text.
   */
  #localizeEndDate() {
    const label = this.querySelector('[data-msc-end-label]');
    if (!label) return;
    try {
      // Store wall clock = end moment + the store's UTC offset at that moment, read as UTC.
      const wallClock = new Date(this.#endMs + this.#offsetSeconds * 1000);
      const lang = document.documentElement.lang || undefined;
      const formatted = new Intl.DateTimeFormat(lang, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'UTC',
      }).format(wallClock);
      label.textContent = `${formatted} ${this.dataset.tzLabel} (${this.dataset.tzOffsetLabel})`;
    } catch (_error) {
      // Server-rendered text stays as-is.
    }
  }

  /**
   * Corrects a wrong device clock using the server's Date header.
   * If this fails, the device clock is used instead.
   */
  async #syncWithServerClock() {
    const signal = this.#abort?.signal;
    try {
      const t0 = Date.now();
      const response = await fetch('/cart.js', { method: 'GET', cache: 'no-store', credentials: 'same-origin', signal });
      const t1 = Date.now();
      const header = response.headers.get('date');
      const serverMs = header ? Date.parse(header) : NaN;
      if (!Number.isFinite(serverMs)) return;
      // The Date header has 1-second resolution; use the midpoint of the request.
      const skew = serverMs + 500 - (t0 + t1) / 2;
      // Only correct for a meaningful drift (> 2s); smaller differences are noise.
      if (Math.abs(skew) > 2000) {
        this.#clockSkewMs = skew;
        this.#update();
      }
    } catch (_error) {
      // Offline, blocked or aborted: keep using the device clock.
    }
  }
}

if (!customElements.get('modoviz-sale-countdown')) {
  customElements.define('modoviz-sale-countdown', ModovizSaleCountdown);
}
